import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';

// Game constants that adapt to screen size
const getGameDimensions = () => {
  const isMobile = window.innerWidth < 768;
  return {
    GAME_HEIGHT: isMobile ? 450 : 600,
    GAME_WIDTH: isMobile ? Math.min(window.innerWidth - 30, 400) : 800,
    ARROW_WIDTH: isMobile ? 40 : 50,
    ARROW_HEIGHT: isMobile ? 40 : 50,
    OBSTACLE_WIDTH: isMobile ? 60 : 80,
    OBSTACLE_HEIGHT: isMobile ? 20 : 30,
    POWERUP_SIZE: isMobile ? 30 : 40
  };
};

const ArjunaArrowTrail = () => {
  // Get initial dimensions
  const initialDimensions = getGameDimensions();
  
  const [gameState, setGameState] = useState('menu'); // menu, playing, gameOver
  const [gameDimensions, setGameDimensions] = useState(initialDimensions);
  const [arrowPosition, setArrowPosition] = useState({ 
    x: initialDimensions.GAME_WIDTH / 2, 
    y: initialDimensions.GAME_HEIGHT - 100 
  });
  const [arrowVelocity, setArrowVelocity] = useState({ x: 0, y: 0 });
  const [obstacles, setObstacles] = useState([]);
  const [powerUps, setPowerUps] = useState([]);
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(localStorage.getItem('arjunaHighScore') || 0);
  const [focusOrbs, setFocusOrbs] = useState(100); // 100% glow
  const [activePowerUp, setActivePowerUp] = useState(null);
  const [powerUpTimer, setPowerUpTimer] = useState(0);
  
  const gameLoopRef = useRef();
  const obstacleSpawnRef = useRef();
  const powerUpSpawnRef = useRef();
  const lastTimeRef = useRef();
  const keysRef = useRef({ left: false, right: false, up: false, down: false });
  const audioContextRef = useRef(null);

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      const dimensions = getGameDimensions();
      setGameDimensions(dimensions);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Initialize audio context
  const initAudio = () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
  };

  // Play sound effects using Web Audio API
  const playSound = (type) => {
    initAudio();
    const audioContext = audioContextRef.current;
    
    if (!audioContext) return;
    
    try {
      switch (type) {
        case 'shoot':
          // Create a shooting sound
          const shootOscillator = audioContext.createOscillator();
          const shootGain = audioContext.createGain();
          
          shootOscillator.connect(shootGain);
          shootGain.connect(audioContext.destination);
          
          shootOscillator.type = 'sine';
          shootOscillator.frequency.setValueAtTime(880, audioContext.currentTime); // A5
          shootOscillator.frequency.exponentialRampToValueAtTime(1760, audioContext.currentTime + 0.2);
          shootGain.gain.setValueAtTime(0.2, audioContext.currentTime);
          shootGain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
          
          shootOscillator.start(audioContext.currentTime);
          shootOscillator.stop(audioContext.currentTime + 0.2);
          break;
          
        case 'collect':
          // Create a pleasant sound for collecting power-ups
          const collectOscillator = audioContext.createOscillator();
          const collectGain = audioContext.createGain();
          
          collectOscillator.connect(collectGain);
          collectGain.connect(audioContext.destination);
          
          collectOscillator.type = 'sine';
          collectOscillator.frequency.setValueAtTime(1046.50, audioContext.currentTime); // C6
          collectGain.gain.setValueAtTime(0.3, audioContext.currentTime);
          collectGain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
          
          collectOscillator.start(audioContext.currentTime);
          collectOscillator.stop(audioContext.currentTime + 0.3);
          break;
          
        case 'hit':
          // Create a sound for hitting obstacles
          const hitOscillator = audioContext.createOscillator();
          const hitGain = audioContext.createGain();
          
          hitOscillator.connect(hitGain);
          hitGain.connect(audioContext.destination);
          
          hitOscillator.type = 'square';
          hitOscillator.frequency.setValueAtTime(220, audioContext.currentTime); // A3
          hitGain.gain.setValueAtTime(0.3, audioContext.currentTime);
          hitGain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
          
          hitOscillator.start(audioContext.currentTime);
          hitOscillator.stop(audioContext.currentTime + 0.3);
          break;
          
        case 'gameOver':
          // Create a game over sound
          const gameOverOscillator = audioContext.createOscillator();
          const gameOverGain = audioContext.createGain();
          
          gameOverOscillator.connect(gameOverGain);
          gameOverGain.connect(audioContext.destination);
          
          gameOverOscillator.type = 'sine';
          gameOverOscillator.frequency.setValueAtTime(174.61, audioContext.currentTime); // F3
          gameOverGain.gain.setValueAtTime(0.3, audioContext.currentTime);
          gameOverGain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 1.0);
          
          gameOverOscillator.start(audioContext.currentTime);
          gameOverOscillator.stop(audioContext.currentTime + 1.0);
          break;
          
        default:
          break;
      }
    } catch (error) {
      console.log('Sound error:', error);
    }
  };

  // Handle keyboard controls
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') keysRef.current.left = true;
      if (e.key === 'ArrowRight') keysRef.current.right = true;
      if (e.key === 'ArrowUp') keysRef.current.up = true;
      if (e.key === 'ArrowDown') keysRef.current.down = true;
    };

    const handleKeyUp = (e) => {
      if (e.key === 'ArrowLeft') keysRef.current.left = false;
      if (e.key === 'ArrowRight') keysRef.current.right = false;
      if (e.key === 'ArrowUp') keysRef.current.up = false;
      if (e.key === 'ArrowDown') keysRef.current.down = false;
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // Handle touch controls for mobile
  const handleTouchStart = useCallback((direction) => {
    if (direction === 'left') keysRef.current.left = true;
    if (direction === 'right') keysRef.current.right = true;
    if (direction === 'up') keysRef.current.up = true;
    if (direction === 'down') keysRef.current.down = true;
  }, []);

  const handleTouchEnd = useCallback((direction) => {
    if (direction === 'left') keysRef.current.left = false;
    if (direction === 'right') keysRef.current.right = false;
    if (direction === 'up') keysRef.current.up = false;
    if (direction === 'down') keysRef.current.down = false;
  }, []);

  // Game loop
  useEffect(() => {
    if (gameState !== 'playing') return;

    const updateGame = (timestamp) => {
      if (!lastTimeRef.current) lastTimeRef.current = timestamp;
      const deltaTime = timestamp - lastTimeRef.current;
      lastTimeRef.current = timestamp;

      // Update arrow position based on velocity and controls
      setArrowPosition(prev => {
        let newX = prev.x;
        let newY = prev.y;
        
        // Apply keyboard controls
        if (keysRef.current.left) newX -= 5;
        if (keysRef.current.right) newX += 5;
        if (keysRef.current.up) newY -= 5;
        if (keysRef.current.down) newY += 5;
        
        // Boundary checks
        newX = Math.max(0, Math.min(gameDimensions.GAME_WIDTH - gameDimensions.ARROW_WIDTH, newX));
        newY = Math.max(0, Math.min(gameDimensions.GAME_HEIGHT - gameDimensions.ARROW_HEIGHT, newY));
        
        return { x: newX, y: newY };
      });

      // Decrease focus orbs over time
      setFocusOrbs(prev => Math.max(0, prev - 0.1));
      
      // If focus orbs are low, slow down the arrow
      const isLowFocus = focusOrbs < 30;
      
      // Move obstacles
      setObstacles(prev => {
        return prev.map(obs => ({
          ...obs,
          x: obs.x + (isLowFocus ? obs.speed * 0.5 : obs.speed)
        })).filter(obs => obs.x < gameDimensions.GAME_WIDTH);
      });
      
      // Move power-ups
      setPowerUps(prev => {
        return prev.map(power => ({
          ...power,
          x: power.x + (isLowFocus ? power.speed * 0.5 : power.speed)
        })).filter(power => power.x < gameDimensions.GAME_WIDTH);
      });

      // Collision detection with obstacles
      const arrowRect = {
        x: arrowPosition.x,
        y: arrowPosition.y,
        width: gameDimensions.ARROW_WIDTH,
        height: gameDimensions.ARROW_HEIGHT
      };

      for (let obstacle of obstacles) {
        const obstacleRect = {
          x: obstacle.x,
          y: obstacle.y,
          width: gameDimensions.OBSTACLE_WIDTH,
          height: gameDimensions.OBSTACLE_HEIGHT
        };

        if (
          arrowRect.x < obstacleRect.x + obstacleRect.width &&
          arrowRect.x + arrowRect.width > obstacleRect.x &&
          arrowRect.y < obstacleRect.y + obstacleRect.height &&
          arrowRect.y + arrowRect.height > obstacleRect.y
        ) {
          // Collision detected
          playSound('hit');
          setFocusOrbs(prev => Math.max(0, prev - 20)); // Lose focus orbs
          
          // Remove the obstacle after collision
          setObstacles(prev => prev.filter(obs => obs !== obstacle));
          break;
        }
      }

      // Collision detection with power-ups
      for (let power of powerUps) {
        const powerRect = {
          x: power.x,
          y: power.y,
          width: gameDimensions.POWERUP_SIZE,
          height: gameDimensions.POWERUP_SIZE
        };

        if (
          arrowRect.x < powerRect.x + powerRect.width &&
          arrowRect.x + arrowRect.width > powerRect.x &&
          arrowRect.y < powerRect.y + powerRect.height &&
          arrowRect.y + arrowRect.height > powerRect.y
        ) {
          // Collect power-up
          playSound('collect');
          
          if (power.type === 'focus') {
            setFocusOrbs(prev => Math.min(100, prev + 30)); // Gain focus orbs
          } else {
            // Activate special power-up
            setActivePowerUp(power.type);
            setPowerUpTimer(300); // 5 seconds at 60fps
          }
          
          setScore(prev => prev + 10);
          setPowerUps(prev => prev.filter(p => p !== power));
          break;
        }
      }

      // Check if focus orbs are depleted
      if (focusOrbs <= 0) {
        setGameState('gameOver');
        playSound('gameOver');
        if (score > highScore) {
          setHighScore(score);
          localStorage.setItem('arjunaHighScore', score);
        }
      }
      
      // Update power-up timer
      if (powerUpTimer > 0) {
        setPowerUpTimer(prev => prev - 1);
        if (powerUpTimer === 1) {
          setActivePowerUp(null);
        }
      }

      gameLoopRef.current = requestAnimationFrame(updateGame);
    };

    gameLoopRef.current = requestAnimationFrame(updateGame);
    return () => {
      if (gameLoopRef.current) cancelAnimationFrame(gameLoopRef.current);
    };
  }, [gameState, arrowPosition, obstacles, powerUps, focusOrbs, score, highScore, gameDimensions, powerUpTimer]);

  // Spawn obstacles
  useEffect(() => {
    if (gameState !== 'playing') return;

    const spawnObstacle = () => {
      const lanes = [50, 150, 250, 350, 450]; // Different lanes for obstacles
      const lane = lanes[Math.floor(Math.random() * lanes.length)];
      const speed = 2 + Math.random() * 3; // Random speed
      
      setObstacles(prev => [
        ...prev,
        {
          id: Date.now(),
          x: -gameDimensions.OBSTACLE_WIDTH,
          y: lane,
          speed: speed
        }
      ]);

      const nextSpawn = Math.random() * 1000 + 500; // 0.5-1.5 seconds
      obstacleSpawnRef.current = setTimeout(spawnObstacle, nextSpawn);
    };

    obstacleSpawnRef.current = setTimeout(spawnObstacle, 1000);

    return () => {
      if (obstacleSpawnRef.current) clearTimeout(obstacleSpawnRef.current);
    };
  }, [gameState, gameDimensions]);

  // Spawn power-ups
  useEffect(() => {
    if (gameState !== 'playing') return;

    const spawnPowerUp = () => {
      const lanes = [80, 180, 280, 380, 480]; // Different lanes for power-ups
      const lane = lanes[Math.floor(Math.random() * lanes.length)];
      const types = ['focus', 'lock', 'flame', 'chant'];
      const type = types[Math.floor(Math.random() * types.length)];
      const speed = 2 + Math.random() * 2; // Random speed
      
      setPowerUps(prev => [
        ...prev,
        {
          id: Date.now() + Math.random(),
          x: -gameDimensions.POWERUP_SIZE,
          y: lane,
          type: type,
          speed: speed
        }
      ]);

      const nextSpawn = Math.random() * 3000 + 2000; // 2-5 seconds
      powerUpSpawnRef.current = setTimeout(spawnPowerUp, nextSpawn);
    };

    powerUpSpawnRef.current = setTimeout(spawnPowerUp, 2000);

    return () => {
      if (powerUpSpawnRef.current) clearTimeout(powerUpSpawnRef.current);
    };
  }, [gameState, gameDimensions]);

  const startGame = () => {
    // Reset dimensions when starting game
    const dimensions = getGameDimensions();
    setGameDimensions(dimensions);
    
    setGameState('playing');
    setArrowPosition({ 
      x: dimensions.GAME_WIDTH / 2, 
      y: dimensions.GAME_HEIGHT - 100 
    });
    setArrowVelocity({ x: 0, y: 0 });
    setObstacles([]);
    setPowerUps([]);
    setScore(0);
    setFocusOrbs(100);
    setActivePowerUp(null);
    setPowerUpTimer(0);
    lastTimeRef.current = null;
  };

  const resetGame = () => {
    setGameState('menu');
  };

  // Get power-up icon based on type
  const getPowerUpIcon = (type) => {
    switch (type) {
      case 'focus': return '🕉️';
      case 'lock': return '🎯';
      case 'flame': return '🔥';
      case 'chant': return '🎵';
      default: return '✨';
    }
  };

  // Get power-up color based on type
  const getPowerUpColor = (type) => {
    switch (type) {
      case 'focus': return 'from-yellow-400 to-yellow-600';
      case 'lock': return 'from-blue-400 to-blue-600';
      case 'flame': return 'from-red-400 to-red-600';
      case 'chant': return 'from-purple-400 to-purple-600';
      default: return 'from-green-400 to-green-600';
    }
  };

  return (
    <div className="flex flex-col items-center w-full">
      <div className="flex items-center justify-center mb-2">
        <img 
          src="/images/avatars/krishna.png" 
          alt="Arjuna" 
          className="w-10 h-10 md:w-12 md:h-12 object-contain mr-3 drop-shadow-md"
          onError={(e) => {
            e.target.src = '/images/avatars/rama.png';
          }}
        />
        <h1 className="text-3xl md:text-4xl font-bold text-blue-600 text-center">Arjuna's Arrow Trail</h1>
      </div>
      <p className="text-base md:text-lg text-gray-600 mb-4 md:mb-6 text-center px-4">
        Guide Arjuna's divine arrow through the aerial maze! Collect Focus Orbs to maintain the divine glow.
      </p>
      
      {/* Game stats */}
      <div className="flex flex-wrap justify-center gap-2 md:gap-4 w-full max-w-2xl mb-4 px-2">
        <div className="bg-blue-100 rounded-full px-3 py-1 md:px-4 md:py-2">
          <span className="font-bold text-blue-600 text-sm md:text-base">Score: {score}</span>
        </div>
        <div className="bg-yellow-100 rounded-full px-3 py-1 md:px-4 md:py-2">
          <span className="font-bold text-yellow-600 text-sm md:text-base">Focus: {Math.round(focusOrbs)}%</span>
        </div>
        <div className="bg-blue-100 rounded-full px-3 py-1 md:px-4 md:py-2">
          <span className="font-bold text-blue-600 text-sm md:text-base">High Score: {highScore}</span>
        </div>
        {activePowerUp && (
          <div className="bg-purple-100 rounded-full px-3 py-1 md:px-4 md:py-2 animate-pulse">
            <span className="font-bold text-purple-600 text-sm md:text-base">
              {getPowerUpIcon(activePowerUp)} {activePowerUp.charAt(0).toUpperCase() + activePowerUp.slice(1)}
            </span>
          </div>
        )}
      </div>
      
      {/* Game container */}
      <div 
        className="relative bg-gradient-to-b from-sky-200 to-sky-400 border-2 md:border-4 border-blue-500 rounded-xl overflow-hidden shadow-xl w-full max-w-full"
        style={{ 
          width: gameDimensions.GAME_WIDTH, 
          height: gameDimensions.GAME_HEIGHT,
          maxWidth: '95vw'
        }}
      >
        {gameState === 'menu' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black bg-opacity-50 z-10 p-2">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-gradient-to-br from-blue-100 to-sky-100 rounded-2xl p-4 md:p-8 text-center max-w-md w-full mx-2 border-2 md:border-4 border-blue-500 shadow-2xl"
            >
              <div className="flex justify-center mb-3 md:mb-5">
                <img 
                  src="/images/avatars/krishna.png" 
                  alt="Arjuna" 
                  className="w-24 h-24 md:w-32 md:h-32 object-contain drop-shadow-lg"
                  onError={(e) => {
                    e.target.src = '/images/avatars/rama.png';
                  }}
                />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-blue-600 mb-2 md:mb-4">Arjuna's Arrow Trail</h2>
              <p className="text-gray-700 text-sm md:text-base mb-2">
                Guide the divine arrow through the aerial maze!
              </p>
              <p className="text-gray-700 text-sm md:text-base mb-4 md:mb-6">
                Use arrow keys to navigate. Collect Focus Orbs to maintain the divine glow!
              </p>
              <button
                onClick={startGame}
                className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 md:py-3 px-6 md:px-8 rounded-full text-lg md:text-xl transition-colors shadow-lg mb-4 md:mb-6 flex items-center justify-center mx-auto"
              >
                <span>Shoot Arrow</span>
                <span className="ml-2">🏹</span>
              </button>
              <div className="bg-white rounded-xl p-3 md:p-4 mb-3 md:mb-4 shadow-md">
                <h3 className="font-bold text-blue-600 mb-2 text-sm md:text-base">Power-ups:</h3>
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex items-center">
                    <span className="text-lg mr-2">🎯</span>
                    <span className="text-xs md:text-sm">Divine Lock</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-lg mr-2">🔥</span>
                    <span className="text-xs md:text-sm">Flaming Arrow</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-lg mr-2">🕉️</span>
                    <span className="text-xs md:text-sm">Focus Orb</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-lg mr-2">🎵</span>
                    <span className="text-xs md:text-sm">Chant Focus</span>
                  </div>
                </div>
              </div>
              <div className="text-gray-500 text-xs md:text-sm">
                <p>Inspired by Arjuna's divine archery from Mahabharata</p>
              </div>
            </motion.div>
          </div>
        )}
        
        {gameState === 'gameOver' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black bg-opacity-50 z-10 p-2">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white rounded-2xl p-4 md:p-8 text-center max-w-md w-full mx-2"
            >
              <h2 className="text-2xl md:text-3xl font-bold text-red-600 mb-2">Game Over!</h2>
              <div className="flex justify-center mb-3 md:mb-5">
                <img 
                  src="/images/avatars/krishna.png" 
                  alt="Arjuna" 
                  className="w-20 h-20 md:w-24 md:h-24 object-contain drop-shadow-lg"
                  onError={(e) => {
                    e.target.src = '/images/avatars/rama.png';
                  }}
                />
              </div>
              <p className="text-xl md:text-2xl text-gray-700 mb-2">Final Score: <span className="font-bold text-blue-600">{score}</span></p>
              {score === parseInt(highScore) && score > 0 && (
                <div className="mb-3 md:mb-4">
                  <p className="text-lg md:text-xl text-yellow-600 font-bold">🎉 New High Score! 🎉</p>
                  <p className="text-base md:text-lg text-yellow-500">You're a master archer!</p>
                  <div className="flex justify-center mt-1 md:mt-2">
                    {[...Array(5)].map((_, i) => (
                      <motion.div
                        key={i}
                        animate={{ 
                          y: [0, -15, 0],
                          rotate: [0, 360]
                        }}
                        transition={{ 
                          duration: 0.5,
                          delay: i * 0.1,
                          repeat: 3
                        }}
                        className="text-xl md:text-2xl mx-1"
                      >
                        🎊
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}
              <div className="bg-blue-50 rounded-xl p-3 md:p-4 mb-4 md:mb-6">
                <h3 className="font-bold text-blue-600 mb-2 text-sm md:text-base">Arjuna's Wisdom:</h3>
                <p className="text-gray-700 italic text-xs md:text-sm">
                  "Focus and precision lead to victory. Practice makes perfect, young archer!"
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center">
                <button
                  onClick={startGame}
                  className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 md:py-3 px-4 md:px-6 rounded-full transition-colors shadow-lg flex items-center justify-center text-sm md:text-base"
                >
                  <span>Try Again</span>
                </button>
                <button
                  onClick={resetGame}
                  className="bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 md:py-3 px-4 md:px-6 rounded-full transition-colors shadow-lg flex items-center justify-center text-sm md:text-base"
                >
                  <span>Main Menu</span>
                </button>
              </div>
            </motion.div>
          </div>
        )}
        
        {/* Sky background elements */}
        <div className="absolute top-0 left-0 w-full h-full">
          {/* Clouds */}
          <div className="absolute top-10 left-10 w-16 h-8 bg-white rounded-full opacity-70"></div>
          <div className="absolute top-20 right-20 w-20 h-10 bg-white rounded-full opacity-70"></div>
          <div className="absolute top-40 left-1/3 w-24 h-12 bg-white rounded-full opacity-70"></div>
        </div>
        
        {/* Arrow */}
        {gameState === 'playing' && (
          <motion.div
            className="absolute"
            style={{
              left: arrowPosition.x,
              top: arrowPosition.y,
              width: gameDimensions.ARROW_WIDTH,
              height: gameDimensions.ARROW_HEIGHT
            }}
            animate={{ 
              scale: focusOrbs < 30 ? [1, 0.9, 1] : 1
            }}
            transition={{ 
              scale: { duration: 0.5, repeat: focusOrbs < 30 ? Infinity : 0 }
            }}
          >
            <div className="w-full h-full flex items-center justify-center relative">
              <div className={`text-3xl md:text-4xl ${focusOrbs < 30 ? 'text-yellow-600' : 'text-yellow-400'} filter drop-shadow-lg`}>
                🏹
              </div>
              {/* Divine glow effect */}
              <div className={`absolute inset-0 rounded-full ${focusOrbs < 30 ? 'bg-yellow-300' : 'bg-yellow-200'} opacity-30 animate-ping`}></div>
            </div>
          </motion.div>
        )}
        
        {/* Obstacles */}
        {obstacles.map(obstacle => (
          <motion.div
            key={obstacle.id}
            className="absolute bg-red-600 rounded-lg flex items-center justify-center border border-red-800"
            style={{
              left: obstacle.x,
              top: obstacle.y,
              width: gameDimensions.OBSTACLE_WIDTH,
              height: gameDimensions.OBSTACLE_HEIGHT
            }}
            whileHover={{ scale: 1.05 }}
          >
            <div className="text-xl">🛡️</div>
          </motion.div>
        ))}
        
        {/* Power-ups */}
        {powerUps.map(power => (
          <motion.div
            key={power.id}
            className={`absolute rounded-full flex items-center justify-center border-2 shadow-lg bg-gradient-to-br ${getPowerUpColor(power.type)}`}
            style={{
              left: power.x,
              top: power.y,
              width: gameDimensions.POWERUP_SIZE,
              height: gameDimensions.POWERUP_SIZE
            }}
            animate={{ 
              y: [0, -10, 0],
              rotate: [0, 360]
            }}
            transition={{ 
              y: { duration: 1, repeat: Infinity },
              rotate: { duration: 3, repeat: Infinity }
            }}
          >
            <div className="text-xl">{getPowerUpIcon(power.type)}</div>
          </motion.div>
        ))}
        
        {/* Focus orbs indicator */}
        <div className="absolute top-4 left-4 w-32 h-4 bg-gray-300 rounded-full">
          <div 
            className={`h-full rounded-full ${focusOrbs > 70 ? 'bg-green-500' : focusOrbs > 30 ? 'bg-yellow-500' : 'bg-red-500'} transition-all duration-300`}
            style={{ width: `${focusOrbs}%` }}
          ></div>
        </div>
      </div>
      
      {/* Mobile controls */}
      <div className="md:hidden mt-4 grid grid-cols-3 gap-2 w-48">
        <div></div>
        <button
          className="bg-blue-500 text-white p-3 rounded-full shadow"
          onTouchStart={() => handleTouchStart('up')}
          onTouchEnd={() => handleTouchEnd('up')}
        >
          ↑
        </button>
        <div></div>
        <button
          className="bg-blue-500 text-white p-3 rounded-full shadow"
          onTouchStart={() => handleTouchStart('left')}
          onTouchEnd={() => handleTouchEnd('left')}
        >
          ←
        </button>
        <div></div>
        <button
          className="bg-blue-500 text-white p-3 rounded-full shadow"
          onTouchStart={() => handleTouchStart('right')}
          onTouchEnd={() => handleTouchEnd('right')}
        >
          →
        </button>
        <div></div>
        <button
          className="bg-blue-500 text-white p-3 rounded-full shadow"
          onTouchStart={() => handleTouchStart('down')}
          onTouchEnd={() => handleTouchEnd('down')}
        >
          ↓
        </button>
        <div></div>
      </div>
      
      {/* Game instructions */}
      <div className="mt-4 md:mt-6 text-center max-w-2xl px-2">
        <div className="bg-blue-100 rounded-xl p-3 md:p-4 mb-3 md:mb-4">
          <p className="text-gray-700 mb-1 md:mb-2 text-sm md:text-base">
            <span className="font-bold text-blue-600">Controls:</span> Use <span className="font-bold">Arrow Keys</span> or <span className="font-bold">On-screen Controls</span> to navigate the arrow
          </p>
          <p className="text-gray-600 text-xs md:text-sm">
            Collect Focus Orbs (🕉️) to maintain the divine glow and avoid obstacles (🛡️)
          </p>
        </div>
        <div className="flex flex-wrap justify-center gap-3 md:gap-8 mt-3 md:mt-4">
          <div className="flex items-center bg-white rounded-full px-3 py-1 md:px-4 md:py-2 shadow">
            <span className="text-lg mr-2">🕉️</span>
            <span className="text-gray-700 text-xs md:text-sm">Focus Orb (+30%)</span>
          </div>
          <div className="flex items-center bg-white rounded-full px-3 py-1 md:px-4 md:py-2 shadow">
            <span className="text-lg mr-2">🎯</span>
            <span className="text-gray-700 text-xs md:text-sm">Divine Lock</span>
          </div>
          <div className="flex items-center bg-white rounded-full px-3 py-1 md:px-4 md:py-2 shadow">
            <span className="text-lg mr-2">🔥</span>
            <span className="text-gray-700 text-xs md:text-sm">Flaming Arrow</span>
          </div>
        </div>
      </div>
      
      {/* Story section */}
      <div className="mt-6 md:mt-8 bg-blue-50 rounded-2xl p-4 md:p-6 max-w-2xl w-full mx-2">
        <h3 className="text-xl md:text-2xl font-bold text-blue-600 mb-2 md:mb-3">The Story of Arjuna's Archery</h3>
        <p className="text-gray-700 mb-2 md:mb-3 text-sm md:text-base">
          During the Mahabharata, Arjuna was the greatest archer of his time. With Lord Krishna as his charioteer, 
          he learned to focus his mind and achieve perfect precision with his arrows.
        </p>
        <p className="text-gray-700 text-sm md:text-base">
          This game celebrates Arjuna's divine skill and the importance of focus and concentration in achieving one's goals.
        </p>
      </div>
    </div>
  );
};

export default ArjunaArrowTrail;